/**
 * Why a Stage could not be read from a Status label. Each one means "do not guess":
 *  - `not-string`  the value is not a string (missing field, null, a number).
 *  - `blank`       empty or whitespace only.
 *  - `no-digit`    the first non-space character is not an ASCII digit 0-9 (this includes signs, full-width and other-script digits).
 *  - `multi-digit` the label starts with two or more digits ("10 X").
 */
export type StageUnknownReason = "blank" | "no-digit" | "multi-digit" | "not-string";

export type StageResult =
  | { readonly kind: "stage"; readonly stage: number }
  | { readonly kind: "unknown"; readonly reason: StageUnknownReason };

/**
 * THE one Service Log Stage rule. Every reader that needs a stage from a Status label imports this; do not write a second parser.
 * Known readers NOT yet on it (different inputs, named in the PR that moved the rule): fieldService/contractCadenceApi.parseStage (reads Stage first, one or two digits),
 * dataHub/appsheet/transformers.extractStage (a write path), executive/operationalMetrics (Project statuses).
 * It lives in `@officehub/shared` so the backends and the client use the SAME function (functions/.../services/serviceLogStage.ts re-exports it).
 *
 * AppSheet computes `Stage = NUMBER(LEFT([Status], 1))`: the FIRST CHARACTER of the label, so "4.1 Dispatched" is Stage 4 (not 4.1)
 * and "1.1 To Quote" is Stage 1. A measurement of the stored mirror against this rule is a derived fact, not stated here: run the
 * Stage shadow report (follow-up to this helper) for the current agreement rate.
 *
 * DELIBERATE, NAMED DIVERGENCES FROM APPSHEET (each fails safe, each is pinned by a test):
 *  1. Two or more leading digits ("10 X"): AppSheet takes one character and reads Stage 1. This rule returns UNKNOWN `multi-digit`:
 *     such a label is a new status nobody has mapped, and calling it Stage 1 would misclassify it for the life of the row (open-ticket
 *     queries compare stages as one-character strings, where "10" < "7" is true). No such status existed when this was written.
 *  2. Leading whitespace (JS `trim()`, which also strips a no-break space and a byte-order mark): AppSheet's LEFT would see the space
 *     as the first character and fail. This rule skips it, so " 6 Reviewed" is Stage 6, the label a person reads. Matches the
 *     `^\s*(\d)` reading used by the schedule-family code.
 *  3. Blank, digit-less or non-string input: AppSheet's NUMBER of such text is an error or 0 ("open"). This rule returns UNKNOWN and
 *     never defaults to open: an unreadable status must not silently count as an open ticket.
 *
 * @param {unknown} status A Service Log Status label, e.g. "4.1 Dispatched".
 * @return {StageResult} The stage (0-9), or UNKNOWN with the reason. Never throws.
 */
export function stageFromStatus(status: unknown): StageResult {
  if (typeof status !== "string") return { kind: "unknown", reason: "not-string" };
  const trimmed = status.trim();
  if (trimmed === "") return { kind: "unknown", reason: "blank" };
  if (!/^\d/.test(trimmed)) return { kind: "unknown", reason: "no-digit" };
  if (/^\d\d/.test(trimmed)) return { kind: "unknown", reason: "multi-digit" };
  return { kind: "stage", stage: Number(trimmed[0]) };
}
